import { Alert, Button } from 'canoe';
import { createCacheKey, ENDPOINTS, useCreatePayload } from 'hammock';
import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useQueryClient } from 'react-query';
import styled from 'styled-components';
import { colors, FlexRow, Footnote, P } from 'styleguide';

// TODO: move away from craft
import { Picture } from 'craft/icons';

import { Loading, uploadImage, uploadImageFromInput } from './uploadImage';

const MegaByte = 1024;

const Wrapper = styled.div`
  align-items: center;
  flex: 0.4;
  flex-direction: column;
  display: flex;
  justify-content: center;
  height: calc(100vh - 200px);
`;

interface DraggableAreaProps {
  isValid: boolean;
  isInvalid: boolean;
}

const getBorderColor = ({ isValid, isInvalid }: DraggableAreaProps) => {
  if (!isValid && !isInvalid) {
    return colors('neutral', 1);
  }

  if (isValid) {
    return colors('secondary');
  }
  return colors('danger', 1);
};

const DraggableArea = styled.div<DraggableAreaProps>`
  align-items: center;
  border: 1px dashed ${getBorderColor};
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-top: 16px;
  overflow: hidden;
  padding: 70px 20px;
  position: relative;
  width: max-content;

  svg {
    margin-bottom: 16px;
  }

  p {
    margin-bottom: 0;
  }
`;

const DraggableButtonWrapper = styled.div`
  overflow: hidden;
  position: relative;
`;

const DragableInput = styled.input`
  bottom: 0;
  cursor: pointer;
  left: 0;
  outline: none;
  padding-left: 300px;
  position: absolute;
  right: 0;
  top: 0;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 20px;
  width: 100%;
`;

interface ProgressProps {
  progress: number;
}

const ProgressBar = styled.div<ProgressProps>`
  background-color: ${colors('white')};
  border-radius: 4px;
  display: block;
  height: 8px;
  overflow: hidden;
  position: relative;
  width: 100%;

  &:after {
    background-color: ${colors('teal')};
    content: '';
    left: 0;
    height: 8px;
    position: absolute;
    top: 0;
    transition: width 1s;
    width: ${({ progress }: ProgressProps) => progress}%;
  }
`;

const getFileSize = (size: number) => {
  if (size < MegaByte) {
    return `${size}bytes`;
  }
  if (size / MegaByte < MegaByte) {
    return `${Math.floor(size / MegaByte)}kb`;
  }
  return `${Math.floor(size / (MegaByte * MegaByte))}mb`;
};

export const MediaUpload = () => {
  const queryClient = useQueryClient();
  const [loaded, setLoadedPercentage] = useState(0);
  const [loading, setLoading] = useState<Loading>({ status: false, error: '', name: '', size: 0 });
  const { getAll } = useCreatePayload(ENDPOINTS.images);

  const reloadImages = ({ src }: any) => {
    if (!src) {
      return;
    }
    queryClient.invalidateQueries(
      createCacheKey(getAll({ query: { finder: 'byOrganizationId' } })),
    );
  };
  const { getRootProps, getInputProps } = useDropzone({
    accept: 'image/*',
    onDrop: uploadImage(reloadImages, setLoading, setLoadedPercentage),
  });

  return (
    <Wrapper>
      {!loading.status && loading.error && (
        <Alert intent="warning" solid={false} scale="small">
          {loading.error}
        </Alert>
      )}
      {loading.name && (
        <LoadingContainer>
          <FlexRow>
            <Footnote color="neutral" shade={2} weight="semibold">
              {loading.name}
            </Footnote>
            <Footnote
              color="neutral"
              shade={2}
              textAlign="right"
              css={{ flex: 1, whiteSpace: 'pre', alignSelf: 'flex-end' }}
            >
              {loading.status ? 'Uploading' : 'Uploaded'}
            </Footnote>
          </FlexRow>
          <ProgressBar progress={loaded} />
          <Footnote color="neutral" shade={2} textAlign="right">
            {loading.size ? getFileSize(loading.size) : null}
          </Footnote>
        </LoadingContainer>
      )}
      {/* @ts-ignore */}
      <DraggableArea {...getRootProps()}>
        <Picture />
        <P>Drag & Drop</P>
        <P fontSize="12px" color="neutral" shade={2}>
          Drag media into this area.
        </P>
        {/* @ts-ignore */}
        <DragableInput
          {...getInputProps()}
          type="file"
          disabled={loading.status}
          accept="image/*"
        />
      </DraggableArea>
      <P fontSize="12px" color="neutral" shade={2} css={{ marginTop: '16px' }}>
        or
      </P>
      <DraggableButtonWrapper>
        <Button variant="primary">Upload Image</Button>
        <DragableInput
          type="file"
          disabled={loading.status}
          onChange={uploadImageFromInput(reloadImages, setLoading, setLoadedPercentage)}
          accept="image/*"
        />
      </DraggableButtonWrapper>
    </Wrapper>
  );
};
